import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProvider } from '@/hooks/useProvider';
import Logo from '../assets/Logo.png';
import { monitor, captureException } from '../lib/monitoring';
import {
  Menu,
  X,
  LogIn,
  User,
  UserPlus,
  Shield,
  ChevronDown,
  LogOut,
  Settings,
  Calendar,
  Clock,
} from 'lucide-react';



// Extracted subcomponents to reduce cognitive complexity (Sonar S3776)
const LoadingNavbar: React.FC = () => {
  return (
    <nav
      className="relative z-50 bg-white border-b border-gray-200 shadow-sm"
    >
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-2 sm:py-3 lg:py-4">
          <div className="flex items-center">
            <img
              src={Logo}
              alt="Baytna Services logo"
              className="h-16 sm:h-20 md:h-24 lg:h-28 w-auto object-contain drop-shadow-lg -translate-y-3 sm:-translate-y-4 md:-translate-y-5"
            />
          </div>
          <div className="animate-pulse text-gray-500">Loading...</div>
        </div>
      </div>
    </nav>
  );
};

interface UserDropdownProps {
  user: any; // kept as any due to existing AuthContext typing
  isOpen: boolean;
  onToggle: () => void;
  onLogout: () => void;
}

const UserDropdown: React.FC<UserDropdownProps> = ({ user, isOpen, onToggle, onLogout }) => (
  <div className="relative">
    <button
      onClick={onToggle}
      aria-haspopup="menu"
      aria-expanded={isOpen}
      aria-label="Open user menu"
      className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-white/30 hover:backdrop-blur-sm transition-colors duration-200"
    >
      <div className="w-9 h-9 bg-gold-gradient rounded-full flex items-center justify-center text-white font-semibold text-sm ring-2 ring-white/40 shadow-md">
        {user.displayName
          ? user.displayName.charAt(0).toUpperCase()
          : user.email?.charAt(0).toUpperCase()}
      </div>
      <span
        className="font-semibold text-red-600 tracking-wide max-w-40 truncate"
        aria-label={user.displayName || user.email?.split('@')[0]}
      >
        {user.displayName || user.email?.split('@')[0]}
      </span>
      <ChevronDown
        size={16}
        className={`text-gold transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
      />
    </button>
    {isOpen && (
      <div
        className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-200/50 py-2 z-50"
        role="menu"
        aria-label="User menu"
      >
        <div className="px-4 py-3 border-b border-gray-100">
          <p className="font-semibold text-red-600 truncate">{user.displayName || 'User'}</p>
          <p className="text-xs text-gray-500 truncate">{user.email}</p>
        </div>
        <Link
          to="/profile"
          className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-50"
          role="menuitem"
          onClick={onToggle}
        >
          <User size={16} />
          My Profile
        </Link>
        <Link
          to="/bookings"
          className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-50"
          role="menuitem"
          onClick={onToggle}
        >
          <Calendar size={16} />
          My Bookings
        </Link>
        <Link
          to="/account"
          className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-50"
          role="menuitem"
          onClick={onToggle}
        >
          <Settings size={16} />
          Settings
        </Link>
        <div className="border-t border-gray-100 mt-2 pt-2">
          <button
            onClick={() => {
              onLogout();
              onToggle();
            }}
            className="flex items-center gap-2 w-full text-left px-4 py-2 text-red-600 hover:bg-red-50"
            role="menuitem"
          >
            <LogOut size={16} />
            Sign Out
          </button>
        </div>
      </div>
    )}
  </div>
);

const Navbar: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [logoutError, setLogoutError] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout, loading } = useAuth();
  const { provider, loading: providerLoading } = useProvider(currentUser?.uid);

  useEffect(() => {
    setOpen(false);
    setUserMenuOpen(false);
  }, [location]);

  // Prevent background scroll when mobile menu is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Ensure mobile menu closes when switching to desktop layout (avoids stuck grey nav on resize)
  useEffect(() => {
    const handleResize = () => {
      if (globalThis.innerWidth >= 1024 && open) {
        setOpen(false);
      }
    };

    globalThis.addEventListener('resize', handleResize);
    return () => {
      globalThis.removeEventListener('resize', handleResize);
    };
  }, [open]);

  const handleLogout = async () => {
    try {
      setLogoutError(null);
      await logout();
      setUserMenuOpen(false);
    } catch (error) {
      // Log for diagnostics and show user-friendly feedback
      monitor.error('Logout failed');
      captureException(error, { where: 'Navbar.handleLogout' });
      setLogoutError('Logout failed, please try again');
    }
  };

  const goProvider = (e?: React.MouseEvent) => {
    e?.preventDefault?.();
    navigate('/provider/register');
  };

  const goAwaitingApproval = () => {
    navigate('/awaiting-approval');
  };

  if (loading) return <LoadingNavbar />;

  const baseClass = 'bg-white border-b border-gray-200 shadow-sm';
  const openClass = open ? 'bg-white/90 backdrop-blur-md border-b border-gray-200 shadow-sm' : '';

  const logoClass =
    'h-16 sm:h-18 md:h-20 lg:h-22 xl:h-24 w-auto object-contain drop-shadow-lg origin-left';

  return (
    <>
      <nav
        className={`relative z-50 ${baseClass} ${openClass} transition-colors duration-300`}
        aria-label="Primary navigation"
      >
        {/* Inline toast for logout errors (accessible, non-blocking) */}
        {logoutError && (
          <div className="absolute right-4 top-2" role="alert" aria-live="polite">
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg shadow-sm">
              <span className="sr-only">Error:</span>
              <span>{logoutError}</span>
              <button
                type="button"
                onClick={handleLogout}
                className="ml-2 underline underline-offset-2 focus:outline-none focus:ring-2 focus:ring-red-400 rounded"
              >
                Try again
              </button>
              <button
                type="button"
                aria-label="Dismiss error"
                onClick={() => setLogoutError(null)}
                className="ml-1 hover:text-red-800"
              >
                ×
              </button>
            </div>
          </div>
        )}
        <div className="w-full px-4 sm:px-6 lg:px-8 relative">
          <div className="flex items-center justify-between py-2 sm:py-3 lg:py-4">
            {/* Logo (improved mobile sizing & tap target) */}
            <Link
              to="/"
              className="flex items-center flex-shrink-0 pt-[env(safe-area-inset-top)]"
              onClick={() => setOpen(false)}
              aria-label="Go to homepage"
            >
              <img
                src={Logo}
                alt="Baytna logo"
                className={logoClass}
                fetchPriority="auto"
                decoding="async"
              />
            </Link>

            {/* Desktop CTA + User */}
            <div className={`hidden lg:flex items-center gap-3 flex-shrink-0`}>
              {/* Provider CTA adapts to status */}
              {providerLoading || !currentUser || !provider || !provider.status ? (
                <button
                  type="button"
                  onClick={goProvider}
                  aria-label="Become a Provider"
                  className="btn-glass-gold h-11 px-5 text-[15px] font-semibold flex items-center gap-2 rounded-xl transition-all duration-200"
                >
                  <Shield size={18} />
                  Become a Provider
                </button>
              ) : provider.status === 'pending' ? (
                <button
                  type="button"
                  onClick={goAwaitingApproval}
                  aria-label="Provider application awaiting approval"
                  className="btn-glass-gold h-11 px-5 text-[15px] font-semibold flex items-center gap-2 rounded-xl transition-all duration-200"
                >
                  <Clock size={18} />
                  Awaiting Approval
                </button>
              ) : provider.status === 'verified' ? (
                <Link
                  to="/profile"
                  aria-label="View your provider profile"
                  className="btn-glass-gold h-11 px-5 text-[15px] font-semibold flex items-center gap-2 rounded-xl transition-all duration-200"
                >
                  <Shield size={18} />
                  Provider Dashboard
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={goProvider}
                  aria-label="Become a Provider"
                  className="btn-glass-gold h-11 px-5 text-[15px] font-semibold flex items-center gap-2 rounded-xl transition-all duration-200"
                >
                  <Shield size={18} />
                  Become a Provider
                </button>
              )}

              {currentUser ? (
                <UserDropdown
                  user={currentUser}
                  isOpen={userMenuOpen}
                  onToggle={() => setUserMenuOpen(!userMenuOpen)}
                  onLogout={handleLogout}
                />
              ) : (
                <>
                  <Link
                    to="/signin"
                    aria-label="Login"
                    className="btn-glass-gold h-11 px-5 text-[15px] font-semibold flex items-center gap-2 rounded-xl transition-all duration-200"
                  >
                    <LogIn size={18} />
                    Login
                  </Link>
                  <Link
                    to="/signup"
                    aria-label="Sign Up"
                    className="btn-glass-gold h-11 px-5 text-[15px] font-semibold flex items-center gap-2 rounded-xl transition-all duration-200"
                  >
                    <UserPlus size={18} />
                    Sign Up
                  </Link>
                </>
              )}
            </div>

            {/* Mobile menu toggle (closer to logo on very small screens) */}
            <button
              className="lg:hidden p-2 ml-2 rounded-xl hover:bg-gray-100 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-red-500 active:scale-95"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? 'Close menu' : 'Open menu'}
              aria-controls="primary-mobile-menu"
              aria-expanded={open}
            >
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu - FULL OVERLAY (independent of navbar height) */}
      {open && (
        <div
          id="primary-mobile-menu"
          className="lg:hidden fixed inset-x-0 top-0 z-[70] flex justify-center pt-[env(safe-area-inset-top)] bg-gradient-to-b from-white/95 to-white/90 backdrop-blur-xl shadow-xl"
          role="menu"
          aria-label="Mobile navigation"
        >
          <div className="relative w-full max-w-md px-4 pb-6 pt-4 space-y-4">
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 text-gray-600 hover:text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-3">
              {/* Mobile provider CTA adapts to status */}
              {providerLoading || !currentUser || !provider || !provider.status ? (
                <button
                  type="button"
                  onClick={() => {
                    goProvider();
                    setOpen(false);
                  }}
                  aria-label="Become a Provider"
                  className="btn-glass-gold h-11 w-full text-[16px] font-semibold flex items-center justify-center gap-2 rounded-xl transition-all duration-200"
                >
                  <Shield size={20} />
                  Become a Provider
                </button>
              ) : provider.status === 'pending' ? (
                <button
                  type="button"
                  onClick={() => {
                    goAwaitingApproval();
                    setOpen(false);
                  }}
                  aria-label="Provider application awaiting approval"
                  className="btn-glass-gold h-11 w-full text-[16px] font-semibold flex items-center justify-center gap-2 rounded-xl transition-all duration-200"
                >
                  <Clock size={20} />
                  Awaiting Approval
                </button>
              ) : provider.status === 'verified' ? (
                <Link
                  to="/profile"
                  aria-label="View your provider profile"
                  className="btn-glass-gold h-11 w-full text-[16px] font-semibold flex items-center justify-center gap-2 rounded-xl transition-all duration-200"
                  role="menuitem"
                  onClick={() => setOpen(false)}
                >
                  <Shield size={20} />
                  Provider Dashboard
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    goProvider();
                    setOpen(false);
                  }}
                  aria-label="Become a Provider"
                  className="btn-glass-gold h-11 w-full text-[16px] font-semibold flex items-center justify-center gap-2 rounded-xl transition-all duration-200"
                >
                  <Shield size={20} />
                  Become a Provider
                </button>
              )}

              {currentUser ? (
                <>
                  <Link
                    to="/profile"
                    className="flex items-center justify-center gap-2 w-full px-4 py-3 border border-gray-300 text-gray-800 font-medium rounded-xl hover:bg-gray-50"
                    role="menuitem"
                    onClick={() => setOpen(false)}
                  >
                    <User size={20} />
                    My Account
                  </Link>
                  <Link
                    to="/account"
                    className="flex items-center justify-center gap-2 w-full px-4 py-3 border border-gray-300 text-gray-800 font-medium rounded-xl hover:bg-gray-50"
                    role="menuitem"
                    onClick={() => setOpen(false)}
                  >
                    <Settings size={20} />
                    Settings
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setOpen(false);
                    }}
                    className="flex items-center justify-center gap-2 w-full px-4 py-3 border border-red-300 text-red-600 font-semibold rounded-xl hover:bg-red-50"
                    role="menuitem"
                  >
                    <LogOut size={20} />
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/signin"
                    aria-label="Login"
                    className="btn-glass-gold h-11 w-full text-[16px] font-semibold flex items-center justify-center gap-2 rounded-xl transition-all duration-200"
                    role="menuitem"
                    onClick={() => setOpen(false)}
                  >
                    <LogIn size={20} />
                    Login
                  </Link>
                  <Link
                    to="/signup"
                    aria-label="Sign Up"
                    className="btn-glass-gold h-11 w-full text-[16px] font-semibold flex items-center justify-center gap-2 rounded-xl transition-all duration-200"
                    role="menuitem"
                    onClick={() => setOpen(false)}
                  >
                    <UserPlus size={20} />
                    Sign Up
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
